import type { EffectParams, ModelConfig, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';

import { Brand, Site } from '../enums';

export interface Help {
  messengerFlag: boolean;
  openChat: boolean;
}

const reducers = {
  set: (
    state: Help,
    payload: {
      messengerFlag: boolean;
    }
  ) => ({ ...state, ...payload }),
  setChat: (
    state: Help,
    payload: {
      openChat: boolean;
    }
  ) => ({ ...state, ...payload })
};

const effects = {
  setMessenger(
    { messengerFlag }: { messengerFlag: boolean },
    { rootState, dispatch }: EffectParams
  ) {
    dispatch.help.set({ messengerFlag });
  },
  setOpenChat({ openChat }: { openChat: boolean }, { rootState, dispatch }: EffectParams) {
    dispatch.help.setChat({ openChat });
  }
};

const selectors = (slice: Slicer<Help>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getShouldOpenMessenger() {
    return createSelector(
      slice,
      (rootState) => select.device.isMobile(rootState),
      (rootState) => select.site.isDesktopEmbedded(rootState),
      (rootState) => select.site.isMobileEmbedded(rootState),
      (rootState) => select.locale.get(rootState),
      (rootState) => select.site.getBrand(rootState),
      ({ messengerFlag }, isMobile, isDesktopEmbedded, isMobileEmbedded, locale, brand) =>
        messengerFlag &&
        !isDesktopEmbedded &&
        !(isMobile && !isMobileEmbedded) &&
        locale === 'en' &&
        (brand === Brand.PokerStars || brand === Brand.StarsCasino)
    );
  },
  getShouldOpenBoldChat() {
    return createSelector(
      slice,
      (rootState) => select.locale.get(rootState),
      (rootState) => select.site.getId(rootState),
      (rootState) => select.site.getBrand(rootState),
      (rootState) => select.site.getRegion(rootState),
      (rootState) => select.device.isMobile(rootState),
      (rootState) => select.location.getLocaleURL(rootState),
      (state, locale, site, brand, region, isMobile, localeURL) => {
        const useDefaultLocale = !localeURL;

        if ([Site.EE, Site.DK].includes(site)) return locale === 'en';

        if (
          [
            Site.IT,
            Site.ES,
            Site.FR,
            Site.PT,
            Site.COM,
            Site.UK,
            Site.NET,
            Site.USPA,
            Site.USNJ
          ].includes(site)
        ) {
          return useDefaultLocale;
        }

        if (site === Site.SE) {
          if (isMobile) return true;

          return useDefaultLocale;
        }

        if (site === Site.EU) {
          if (brand === Brand.PokerStarsVegas || region === 'DE') return useDefaultLocale; // site: pokerstarsvegas.de or pokerstars.de

          return locale === 'de' || locale === 'en'; // site: pokerstars.eu
        }

        return false;
      }
    );
  }
});

export interface HelpModel extends ModelConfig<Help> {
  state: Help;
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const help: HelpModel = {
  state: { messengerFlag: true, openChat: false },
  reducers,
  effects,
  selectors
};

export default help;
